import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from 'recharts';
import Layout from '../components/Layout';
import CompanyFilter from '../components/CompanyFilter';
import { api, formatCurrency } from '../lib/api';

const Reports = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [ageBuckets, setAgeBuckets] = useState([]);
  const [topClients, setTopClients] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/companies').then(res => setCompanies(res.data));
  }, []);

  useEffect(() => {
    const params = selectedCompany ? { company_id: selectedCompany } : {};
    api.get('/reports/age-buckets', { params }).then(res => setAgeBuckets(res.data));
    api.get('/reports/top-clients', { params: { ...params, limit: 5 } }).then(res => setTopClients(res.data));
  }, [selectedCompany]);

  const ageColors = ['#2A4A35', '#B8A96A', '#7A2438', '#5C1A2A'];
  const totalOutstanding = ageBuckets.reduce((s, b) => s + b.amount, 0);

  return (
    <Layout title="Reports" action={<CompanyFilter companies={companies} selected={selectedCompany} onChange={setSelectedCompany} />}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Age Buckets */}
        <div data-testid="age-bucket-card" className="bg-surface border border-border p-6 fade-up">
          <div className="flex justify-between items-baseline mb-1">
            <div>
              <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary">Receivables Aging</p>
              <h3 className="font-heading text-xl font-light text-text-primary">Outstanding by Age</h3>
            </div>
            <p className="font-numbers text-xl text-maroon tabular-nums">{formatCurrency(totalOutstanding)}</p>
          </div>

          <div style={{ width: '100%', height: 240, marginTop: 24 }}>
            <ResponsiveContainer>
              <BarChart data={ageBuckets} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                <CartesianGrid stroke="#D4CEC6" strokeDasharray="0" vertical={false} />
                <XAxis dataKey="bucket" stroke="#4A4A4A" tick={{ fontFamily: 'Outfit', fontSize: 11, fontWeight: 300 }} axisLine={{ stroke: '#D4CEC6' }} tickLine={false} />
                <YAxis stroke="#4A4A4A" tick={{ fontFamily: 'EB Garamond', fontSize: 11 }} axisLine={{ stroke: '#D4CEC6' }} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ background: '#EDE8E0', border: '1px solid #D4CEC6', borderRadius: 0, fontFamily: 'Outfit', fontWeight: 300 }}
                  formatter={(v) => formatCurrency(v)}
                />
                <Bar dataKey="amount" name="Outstanding">
                  {ageBuckets.map((entry, i) => <Cell key={i} fill={ageColors[i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-4 gap-3 mt-4 pt-4 border-t border-border">
            {ageBuckets.map((b, i) => (
              <div key={b.bucket} className="text-center">
                <div className="w-2 h-2 mx-auto mb-2" style={{ background: ageColors[i] }} />
                <p className="font-body text-xs uppercase tracking-widest text-text-secondary">{b.bucket}d</p>
                <p className="font-numbers text-sm tabular-nums mt-1" style={{ color: i < 2 ? '#1A1A1A' : '#5C1A2A' }}>{formatCurrency(b.amount)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Top Clients */}
        <div data-testid="top-clients-card" className="bg-surface border border-border p-6 fade-up fade-up-d1">
          <div className="mb-4">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-text-secondary">Revenue Leaders</p>
            <h3 className="font-heading text-xl font-light text-text-primary">Top 5 Clients</h3>
          </div>
          <div className="space-y-4">
            {topClients.map((c, i) => {
              const collectionRate = c.invoiced > 0 ? (c.collected / c.invoiced) * 100 : 0;
              return (
                <div key={c.id} className="cursor-pointer hover:bg-cream p-3 -mx-3 transition-colors border-b border-border last:border-0" onClick={() => navigate(`/clients/${c.id}`)}>
                  <div className="flex justify-between items-baseline mb-2">
                    <div className="flex items-center">
                      <span className="font-heading text-2xl text-maroon mr-3 mono-num">{String(i + 1).padStart(2, '0')}</span>
                      <div>
                        <p className="font-body text-sm text-text-primary">{c.name}</p>
                        <p className="font-body text-xs text-text-secondary mono-num">{c.invoice_count} invoices</p>
                      </div>
                    </div>
                    <p className="font-numbers text-lg text-text-primary tabular-nums">{formatCurrency(c.invoiced)}</p>
                  </div>
                  <div className="relative h-1 bg-border w-full mt-2">
                    <div className="absolute left-0 top-0 h-full bg-success" style={{ width: `${collectionRate}%` }} />
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="font-body text-xs text-text-secondary mono-num">{Math.round(collectionRate)}% collected</span>
                    {c.outstanding > 0 && <span className="font-body text-xs text-maroon mono-num">{formatCurrency(c.outstanding)} due</span>}
                  </div>
                </div>
              );
            })}
            {topClients.length === 0 && <p className="text-center py-8 font-body text-sm text-text-secondary">No data</p>}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Reports;
